import { forwardRef, HTMLProps, ReactNode } from 'react';

import { tv } from 'tailwind-variants';

import Spinner from '@/components/V2/Spinner';
import cn from '@/helpers/utils/classNames';

type CustomButton = Omit<HTMLProps<HTMLButtonElement>, 'type'>;

export interface ButtonProps extends CustomButton {
  scheme: 'primary' | 'secondary';
  variant: 'outlined' | 'filled';
  type: 'button' | 'submit';
  loading?: boolean;
  children: ReactNode;
}

export const buttonStyles = tv({
  base: 'border whitespace-nowrap transition-colors p-1 px-4 text-base hover:transition-colors rounded-xs py-2',
  variants: {
    primary: {
      filled:
        'border-transparent text-white bg-primary outline-grayscale-40 hover:text-primary hover:border-primary hover:bg-white active:shadow-button active:bg-none active:outline-0',
      outlined: 'bg-white text-primary border-primary active:shadow-button active:bg-none active:outline-0',
    },
    secondary: {
      filled:
        'border-transparent text-white bg-grayscale-40 hover:text-grayscale-40 outline-primary hover:border-grayscale-40 hover:bg-transparent',
      outlined: 'bg-transparent text-grayscale-70 border-grayscale-30 hover:text-primary',
    },
  },
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { scheme, type, variant, disabled, loading, className, children, ...restProps } = props;

  return (
    <>
      <button
        ref={ref}
        type={type || 'button'}
        className={cn(
          buttonStyles({ [scheme]: variant }),
          {
            'cursor-not-allowed border-transparent bg-grayscale-60 text-white opacity-30 hover:border-transparent hover:bg-grayscale-70 hover:text-white focus:bg-grayscale-70 focus:text-white':
              disabled,
          },
          {
            'pointer-events-none flex cursor-not-allowed justify-center border-transparent bg-[#F2F4F7] text-white hover:border-transparent hover:bg-[#F2F4F7]':
              loading,
          },
          className
        )}
        disabled={!!disabled}
        {...restProps}
      >
        {loading ? <Spinner /> : children}
      </button>
    </>
  );
});

export default Button;
